import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled from 'styled-components'
import { PiArrowSquareOut } from 'react-icons/pi'

// Styled tooltip that matches design
const StyledTooltip = styled(Tooltip)`
  &.show {
    opacity: 1 !important;
  }

  & .tooltip-inner {
    background-color: white !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 6px !important;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    padding: 6px 8px !important;
    font-size: 12px !important;
    max-width: none !important;
    text-align: left !important;
    color: #111827 !important;
  }

  &.bs-tooltip-top .tooltip-arrow::before {
    border-top-color: #e5e7eb !important;
  }

  &.bs-tooltip-top .tooltip-arrow::after {
    border-top-color: white !important;
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    border: 5px solid transparent;
    border-top-color: white;
  }
`

interface ChartItem {
  name: string
  value: number
  color: string
  percentage: number
}

interface LegendConfig {
  title?: string
  showLink?: boolean
  linkHref?: string
  columns?: 1 | 2
  showPercentages?: boolean
}

interface ProgressChartProps {
  // Title variants
  label?: string // Forecast style: "ANNUAL REVENUE OPP."
  value?: string // Forecast style: "$24.3K"
  title?: string // Demographic style: "Age"

  // Chart data
  items: ChartItem[]

  // Chart options
  showScale?: boolean // Show 0%-100% labels
  tooltipValueFormatter?: (item: ChartItem) => string // Custom tooltip value formatter

  // Legend configuration
  legend?: LegendConfig

  // Styling
  className?: string
}

function TooltipContent({ title, value }: { title: string; value: string }) {
  return (
    <div>
      <div style={{ fontWeight: 500, color: '#111827' }}>{title}</div>
      <div style={{ fontWeight: 300, color: '#111827' }}>{value}</div>
    </div>
  )
}

function LegendItem({
  item,
  showPercentage = false,
}: {
  item: ChartItem
  showPercentage?: boolean
}) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: item.color }}
      />
      <div
        className="text-xs font-light text-gray-900 truncate min-w-0 flex-1 tabular-nums"
        title={
          showPercentage ? `${item.name} - ${item.percentage}%` : item.name
        }
      >
        {item.name}
        {showPercentage && <span> - {item.percentage}%</span>}
      </div>
    </div>
  )
}

export function ProgressChart({
  label,
  value,
  title,
  items,
  showScale = false,
  tooltipValueFormatter,
  legend,
  className = '',
}: ProgressChartProps) {
  const hasLegend = legend && items.length > 0

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Title Section */}
      {(label || title) && (
        <div className="flex flex-col gap-2">
          {label && (
            <div className="text-xs font-light text-gray-900 uppercase tracking-wider">
              {label}
            </div>
          )}
          {value && (
            <div className="text-base font-semibold text-gray-900 tracking-tight tabular-nums">
              {value}
            </div>
          )}
          {title && (
            <div className="text-base font-semibold text-gray-900 tracking-tight">
              {title}
            </div>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div className={showScale ? 'mb-3' : ''}>
        <div className="w-full h-4 bg-gray-100 rounded-sm overflow-hidden flex">
          {items.map((item, index) => (
            <OverlayTrigger
              key={item.name}
              placement="top"
              overlay={
                <StyledTooltip id={`tooltip-${item.name}`}>
                  <TooltipContent
                    title={item.name}
                    value={
                      tooltipValueFormatter
                        ? tooltipValueFormatter(item)
                        : legend?.showPercentages
                          ? `${item.percentage}%`
                          : item.value.toString()
                    }
                  />
                </StyledTooltip>
              }
            >
              <div
                className={`h-full cursor-pointer ${index === 0 ? 'rounded-l-sm' : ''} ${
                  index === items.length - 1 ? 'rounded-r-sm' : ''
                }`}
                style={{
                  backgroundColor: item.color,
                  width: `${item.percentage}%`,
                }}
              />
            </OverlayTrigger>
          ))}
        </div>

        {/* Scale Labels */}
        {showScale && (
          <div className="flex justify-between mt-1">
            <span className="text-xs font-light text-gray-500 uppercase tracking-wider">
              0%
            </span>
            <span className="text-xs font-light text-gray-500 uppercase tracking-wider">
              100%
            </span>
          </div>
        )}
      </div>

      {/* Legend */}
      {hasLegend && (
        <div className="bg-white rounded-lg p-3 shadow-sm mt-2">
          {/* Legend Header */}
          {(legend.title || legend.showLink) && (
            <div className="flex items-center justify-between mb-3">
              {legend.title && (
                <span className="text-sm font-medium text-gray-900">
                  {legend.title}
                </span>
              )}
              {legend.showLink && legend.linkHref && (
                <a
                  href={legend.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-bright hover:text-brand-active transition-colors"
                >
                  <PiArrowSquareOut size={14} />
                </a>
              )}
            </div>
          )}

          {/* Legend Items */}
          <div
            className={
              legend.columns === 2 ? 'grid grid-cols-2 gap-2' : 'space-y-2'
            }
          >
            {items.map((item) => (
              <LegendItem
                key={item.name}
                item={item}
                showPercentage={legend.showPercentages}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
